import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';

const API = `${window.location.origin}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [resetComplete, setResetComplete] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      toast.error('Token de recuperación no válido');
      navigate('/forgot-password');
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwords.newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: passwords.newPassword
      });

      if (response.data.success) {
        setResetComplete(true);
        toast.success('Contraseña restablecida exitosamente');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Error al restablecer contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  if (resetComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center bg-gradient-to-r from-green-500 to-green-600 text-white">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-check text-green-500 text-2xl"></i>
            </div>
            <CardTitle className="text-2xl">¡Contraseña Restablecida!</CardTitle>
          </CardHeader>
          <CardContent className="p-6 text-center">
            <p className="text-gray-600 mb-6">
              Tu contraseña ha sido restablecida exitosamente. 
              Ya puedes iniciar sesión con tu nueva contraseña.
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center">
                <i className="fas fa-shield-alt text-green-500 mr-3"></i>
                <div>
                  <h4 className="font-semibold text-green-800">Seguridad</h4>
                  <p className="text-sm text-green-600">
                    Por tu seguridad, todos los tokens de recuperación han sido invalidados.
                  </p>
                </div>
              </div>
            </div>

            <Link to="/app">
              <Button className="w-full instituto-gradient text-white">
                <i className="fas fa-sign-in-alt mr-2"></i>
                Iniciar Sesión
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-lock text-purple-500 text-2xl"></i>
          </div>
          <CardTitle className="text-2xl">Nueva Contraseña</CardTitle>
          <p className="text-purple-100 mt-2">
            Instituto Bíblico Apostólico del Uruguay
          </p>
        </CardHeader>
        <CardContent className="p-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <i className="fas fa-info-circle text-blue-500 mt-1 mr-3"></i>
              <div>
                <h4 className="font-semibold text-blue-800 mb-1">Crear Nueva Contraseña</h4>
                <p className="text-sm text-blue-600">
                  Ingresa una nueva contraseña segura para tu cuenta. 
                  Debe tener al menos 6 caracteres.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nueva Contraseña *</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPasswords.new ? "text" : "password"}
                  value={passwords.newPassword}
                  onChange={(e) => setPasswords(prev => ({...prev, newPassword: e.target.value}))}
                  required
                  minLength={6}
                  className="pr-10"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={isLoading}
                >
                  <i className={`fas ${showPasswords.new ? 'fa-eye-slash' : 'fa-eye'} text-gray-400`}></i>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Nueva Contraseña *</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwords.confirmPassword}
                  onChange={(e) => setPasswords(prev => ({...prev, confirmPassword: e.target.value}))}
                  required
                  minLength={6}
                  className="pr-10"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={isLoading}
                >
                  <i className={`fas ${showPasswords.confirm ? 'fa-eye-slash' : 'fa-eye'} text-gray-400`}></i>
                </button>
              </div>
            </div>

            {passwords.newPassword && passwords.confirmPassword && 
             passwords.newPassword !== passwords.confirmPassword && (
              <p className="text-sm text-red-600">Las contraseñas no coinciden</p>
            )}

            {/* Password strength indicator */}
            {passwords.newPassword && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">Fortaleza de la contraseña:</p>
                <div className="flex space-x-1">
                  <div className={`h-2 w-1/3 rounded ${passwords.newPassword.length >= 6 ? 'bg-red-500' : 'bg-gray-200'}`}></div>
                  <div className={`h-2 w-1/3 rounded ${passwords.newPassword.length >= 8 ? 'bg-yellow-500' : 'bg-gray-200'}`}></div>
                  <div className={`h-2 w-1/3 rounded ${passwords.newPassword.length >= 10 && /[0-9]/.test(passwords.newPassword) && /[A-Z]/.test(passwords.newPassword) ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                </div>
                <p className="text-xs text-gray-500">
                  {passwords.newPassword.length < 6 ? 'Muy débil' :
                   passwords.newPassword.length < 8 ? 'Débil' :
                   passwords.newPassword.length >= 10 && /[0-9]/.test(passwords.newPassword) && /[A-Z]/.test(passwords.newPassword) ? 'Fuerte' : 'Moderada'}
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading || !passwords.newPassword || !passwords.confirmPassword ||
                       passwords.newPassword !== passwords.confirmPassword}
              className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-3"
            >
              <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : 'fa-save'} mr-2`}></i>
              {isLoading ? 'Restableciendo...' : 'Restablecer Contraseña'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/app">
              <Button variant="outline" className="w-full">
                <i className="fas fa-arrow-left mr-2"></i>
                Cancelar y Volver al Login
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;